import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
export function WorkRelationshipDecodeInputs() {
  const navigate = useNavigate();
  const [showEntry, setShowEntry] = useState(true);
  const [currentPhase, setCurrentPhase] = useState<1 | 2 | 3 | 4>(1);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showEndScreen, setShowEndScreen] = useState(false);
  const [formData, setFormData] = useState({
    // Phase 1: Authority Context
    personRelation: '',
    trigger: '',
    // Phase 2: Power & Risk
    controlOutcomes: '',
    implicitRisk: '',
    // Phase 3: Adaptive Response
    usualResponse: '',
    // Phase 4: Loop Outcome
    afterOutcome: '',
    // Optional
    additionalContext: ''
  });
  const handleOptionSelect = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleStartDiagnostic = () => {
    setShowEntry(false);
  };
  const handleNext = () => {
    // Phase 1 navigation
    if (currentPhase === 1) {
      if (currentStep === 1 && !formData.personRelation) return;
      if (currentStep === 2 && !formData.trigger) return;
      if (currentStep < 2) {
        setCurrentStep(currentStep + 1);
      } else {
        setCurrentPhase(2);
        setCurrentStep(1);
      }
    }
    // Phase 2 navigation
    else if (currentPhase === 2) {
      if (currentStep === 1 && !formData.controlOutcomes) return;
      if (currentStep === 2 && !formData.implicitRisk) return;
      if (currentStep < 2) {
        setCurrentStep(currentStep + 1);
      } else {
        setCurrentPhase(3);
        setCurrentStep(1);
      }
    }
    // Phase 3 navigation
    else if (currentPhase === 3) {
      if (currentStep === 1 && !formData.usualResponse) return;
      setCurrentPhase(4);
      setCurrentStep(1);
    }
    // Phase 4 navigation
    else if (currentPhase === 4) {
      if (currentStep === 1 && !formData.afterOutcome) return;
      setShowEndScreen(true);
    }
  };
  const handleGenerateReport = () => {
    setIsGenerating(true);
    console.log('Work Relationship Decode inputs:', formData);
    // Simulate report generation
    setTimeout(() => {
      navigate('/report-output');
    }, 3000);
  };
  // Phase 1 options - Role-neutral
  const personRelationOptions = ['Manager or leader', 'Senior leadership', 'Founder or owner', 'Client or external authority', 'Peer or colleague', 'Direct report'];
  const triggerOptions = ['Performance pressure', 'Feedback or evaluation', 'Conflict or disagreement', 'Decision blockage', 'Ongoing tension'];
  // Phase 2 options
  const controlOutcomesOptions = ['Mostly them', 'Roughly equal', 'Mostly me', 'Depends on the situation'];
  const implicitRiskOptions = ['Making a mistake', 'Pushing back', 'Disappointing them', 'Being misunderstood', 'Losing trust or standing'];
  // Phase 3 options
  const usualResponseOptions = ['Over-deliver or compensate', 'Explain or justify decisions', 'Hold back or wait', 'Push harder', 'Disengage internally'];
  // Phase 4 options
  const afterOutcomeOptions = ['Tension reduces temporarily', 'Pressure increases', 'They take over decisions', 'I disengage', 'Nothing really changes'];
  if (showEntry) {
    return <>
        <Helmet data-id="element-4727">
          <title data-id="element-4728">
            Work Relationship Decode — Diagnostic Input | Lazy Monk Labs
          </title>
          <meta name="robots" content="noindex, nofollow" data-id="element-4729" />
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex flex-col" data-id="element-4730">
          <header data-id="element-4731">
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-4732">
              <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-4733">
                <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-4734">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-4735" />
                  <span className="text-lg font-medium tracking-tight" data-id="element-4736">
                    Lazy Monk Labs
                  </span>
                </Link>
              </div>
            </nav>
          </header>

          <main className="flex-1 flex items-center justify-center px-6 pt-20 pb-16" data-id="element-4737">
            <div className="max-w-2xl mx-auto text-center w-full" data-id="element-4738">
              <h1 className="text-4xl md:text-5xl font-medium mb-6 leading-tight tracking-tight" data-id="element-4739">
                Work Relationship Decode — diagnostic input
              </h1>
              <p className="text-[#F5F5F5]/60 mb-12 leading-relaxed max-w-xl mx-auto" data-id="element-4740">
                Answering these helps map the work relationship dynamic
                accurately.
                <br data-id="element-4741" />
                This is not an evaluation of competence.
              </p>
              <button onClick={handleStartDiagnostic} className="px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-4742">
                Start Diagnostic
              </button>
            </div>
          </main>
        </div>
      </>;
  }
  if (isGenerating) {
    return <>
        <Helmet data-id="element-4743">
          <title data-id="element-4744">
            Generating Report — Work Relationship Decode | Lazy Monk Labs
          </title>
          <meta name="robots" content="noindex, nofollow" data-id="element-4745" />
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-6" data-id="element-4746">
          <div className="text-center" data-id="element-4747">
            <p className="text-xl text-[#F5F5F5]/70" data-id="element-4748">Analyzing patterns...</p>
          </div>
        </div>
      </>;
  }
  if (showEndScreen) {
    return <>
        <Helmet data-id="element-4749">
          <title data-id="element-4750">
            Ready to Generate — Work Relationship Decode | Lazy Monk Labs
          </title>
          <meta name="robots" content="noindex, nofollow" data-id="element-4751" />
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex flex-col" data-id="element-4752">
          <header data-id="element-4753">
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-4754">
              <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-4755">
                <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-4756">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-4757" />
                  <span className="text-lg font-medium tracking-tight" data-id="element-4758">
                    Lazy Monk Labs
                  </span>
                </Link>
              </div>
            </nav>
          </header>

          <main className="flex-1 flex items-center justify-center px-6 pt-20 pb-16" data-id="element-4759">
            <div className="max-w-2xl mx-auto w-full" data-id="element-4760">
              <h1 className="text-4xl md:text-5xl font-medium mb-6 text-center leading-tight tracking-tight" data-id="element-4761">
                Ready to generate your Work Relationship Decode
              </h1>
              <p className="text-[#F5F5F5]/60 mb-8 leading-relaxed text-center" data-id="element-4762">
                Work relationship context, power dynamics, and response loops
                have been captured.
              </p>

              {/* Optional Context Field */}
              <div className="mb-12" data-id="element-4763">
                <label htmlFor="additionalContext" className="block text-sm font-medium mb-3" data-id="element-4764">
                  Anything specific you want this report to consider? (optional)
                </label>
                <textarea id="additionalContext" value={formData.additionalContext} onChange={e => handleOptionSelect('additionalContext', e.target.value)} maxLength={150} rows={3} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors resize-none" data-id="element-4765" />
                <p className="text-xs text-[#F5F5F5]/40 mt-2 text-right" data-id="element-4766">
                  {formData.additionalContext.length}/150
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-4767">
                <button onClick={handleGenerateReport} className="px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-4768">
                  Generate Full Report
                </button>
                <button onClick={() => {
                setShowEndScreen(false);
                setCurrentPhase(1);
                setCurrentStep(1);
              }} className="px-8 py-5 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors" data-id="element-4769">
                  Edit responses
                </button>
              </div>
            </div>
          </main>
        </div>
      </>;
  }
  return <>
      <Helmet data-id="element-4770">
        <title data-id="element-4771">
          Work Relationship Decode — Diagnostic Input | Lazy Monk Labs
        </title>
        <meta name="robots" content="noindex, nofollow" data-id="element-4772" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex flex-col" data-id="element-4773">
        <header data-id="element-4774">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-4775">
            <div className="max-w-7xl mx-auto px-6 py-4" data-id="element-4776">
              <p className="text-xs text-[#F5F5F5]/50 text-center tracking-wider" data-id="element-4777">
                Diagnostic progress — Phase {currentPhase} of 4
              </p>
            </div>
          </nav>
        </header>

        <main className="flex-1 flex items-center justify-center px-6 pt-20 pb-16" data-id="element-4778">
          <div className="max-w-2xl mx-auto w-full" data-id="element-4779">
            {/* Phase 1: Authority Context */}
            {currentPhase === 1 && currentStep === 1 && <div data-id="element-4780">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-4781">
                  Who is this person in relation to you?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-4782">
                  {personRelationOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.personRelation === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-4783">
                      <input type="radio" name="personRelation" value={option} checked={formData.personRelation === option} onChange={e => handleOptionSelect('personRelation', e.target.value)} className="sr-only" data-id="element-4784" />
                      <span className="text-base" data-id="element-4785">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.personRelation} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-4786">
                  Continue
                </button>
              </div>}

            {currentPhase === 1 && currentStep === 2 && <div data-id="element-4787">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-4788">
                  What triggered this decode?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-4789">
                  {triggerOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.trigger === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-4790">
                      <input type="radio" name="trigger" value={option} checked={formData.trigger === option} onChange={e => handleOptionSelect('trigger', e.target.value)} className="sr-only" data-id="element-4791" />
                      <span className="text-base" data-id="element-4792">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.trigger} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-4793">
                  Continue
                </button>
              </div>}

            {/* Phase 2: Power & Risk */}
            {currentPhase === 2 && currentStep === 1 && <div data-id="element-4794">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-4795">
                  Who ultimately controls outcomes here?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-4796">
                  {controlOutcomesOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.controlOutcomes === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-4797">
                      <input type="radio" name="controlOutcomes" value={option} checked={formData.controlOutcomes === option} onChange={e => handleOptionSelect('controlOutcomes', e.target.value)} className="sr-only" data-id="element-4798" />
                      <span className="text-base" data-id="element-4799">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.controlOutcomes} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-4800">
                  Continue
                </button>
              </div>}

            {currentPhase === 2 && currentStep === 2 && <div data-id="element-4801">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-4802">
                  What feels implicitly risky in this dynamic?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-4803">
                  {implicitRiskOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.implicitRisk === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-4804">
                      <input type="radio" name="implicitRisk" value={option} checked={formData.implicitRisk === option} onChange={e => handleOptionSelect('implicitRisk', e.target.value)} className="sr-only" data-id="element-4805" />
                      <span className="text-base" data-id="element-4806">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.implicitRisk} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-4807">
                  Continue
                </button>
              </div>}

            {/* Phase 3: Adaptive Response */}
            {currentPhase === 3 && currentStep === 1 && <div data-id="element-4808">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-4809">
                  When this dynamic activates, what do you usually do?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-4810">
                  {usualResponseOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.usualResponse === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-4811">
                      <input type="radio" name="usualResponse" value={option} checked={formData.usualResponse === option} onChange={e => handleOptionSelect('usualResponse', e.target.value)} className="sr-only" data-id="element-4812" />
                      <span className="text-base" data-id="element-4813">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.usualResponse} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-4814">
                  Continue
                </button>
              </div>}

            {/* Phase 4: Loop Outcome */}
            {currentPhase === 4 && currentStep === 1 && <div data-id="element-4815">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-4816">
                  What usually happens right after?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-4817">
                  {afterOutcomeOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.afterOutcome === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-4818">
                      <input type="radio" name="afterOutcome" value={option} checked={formData.afterOutcome === option} onChange={e => handleOptionSelect('afterOutcome', e.target.value)} className="sr-only" data-id="element-4819" />
                      <span className="text-base" data-id="element-4820">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.afterOutcome} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-4821">
                  Continue
                </button>
              </div>}
          </div>
        </main>
      </div>
    </>;
}